import React, { useEffect, useState } from 'react'
import AdminLayout from '@/components/AdminLayout'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Plus,
  Search,
  Pencil,
  Trash2,
  BookOpen,
  Users,
  LayoutGrid,
  ListIcon,
  Copy,
} from 'lucide-react'
import { Link, useNavigate } from 'react-router-dom'
import TestForm from '@/components/TestForm'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { supabase } from '@/integrations/supabase/client'
import toast from 'react-hot-toast'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { getPaginationPages, getTotalPages, paginate } from '@/utils/pagination'

const AdminTests = () => {
  const navigate = useNavigate()

  const [tests, setTests] = useState([])
  const [loading, setLoading] = useState(true)
  const [isAddingTest, setIsAddingTest] = useState(false)
  const [editingTest, setEditingTest] = useState(null)
  const [searchInput, setSearchInput] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [questionCounts, setQuestionCounts] = useState({})
  const [assignmentCounts, setAssignmentCounts] = useState({})
  const [completedCounts, setCompletedCounts] = useState({})
  const [totalTestCount, setTotalTestCount] = useState(0)
  const [countsLoading, setCountsLoading] = useState(true)
  const [viewMode, setViewMode] = useState('card') // 'card' | 'list'
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(9)

  useEffect(() => {
    fetchTests()
  }, [])

  const fetchTests = async () => {
    setLoading(true)
    setCountsLoading(true)

    const { data, error } = await supabase
      .from('tests')
      .select('id, name, description, type, created_at')
      .order('created_at', { ascending: false })

    if (error) {
      toast.error('Sınavlar yüklenirken hata oluştu')
      setLoading(false)
      setCountsLoading(false)
      return
    }

    setTests(data || [])
    setLoading(false)

    fetchCountsForTests()
  }

  const fetchCountsForTests = async () => {
    setCountsLoading(true)

    const { data, error } = await supabase.rpc('get_test_counts')

    if (error) {
      toast.error('Atama sayıları alınamadı')
      setCountsLoading(false)
      return
    }

    const aCounts: Record<string, number> = {}
    const cCounts: Record<string, number> = {}
    const qCounts: Record<string, number> = {}
    let total = 0

    for (const row of data ?? []) {
      aCounts[row.test_id] = Number(row.assignment_count)
      cCounts[row.test_id] = Number(row.completed_count)
      qCounts[row.test_id] = Number(row.question_count)
      total += Number(row.assignment_count)
    }

    setAssignmentCounts(aCounts)
    setCompletedCounts(cCounts)
    setQuestionCounts(qCounts)
    setTotalTestCount(total)
    setCountsLoading(false)
  }

  const handleSearch = () => {
    setSearchQuery(searchInput)
    setPage(1)
  }

  const filteredTests = tests.filter(
    (test) =>
      test.name.toLocaleLowerCase('tr').includes(searchQuery.toLocaleLowerCase('tr')) ||
      test.description?.toLocaleLowerCase('tr').includes(searchQuery.toLocaleLowerCase('tr'))
  )

  const handleAddTest = () => {
    setIsAddingTest(true)
    setEditingTest(null)
  }

  const handleTestEdit = (test) => {
    setEditingTest(test)
    setIsAddingTest(true)
  }

  const handleFormComplete = () => {
    setIsAddingTest(false)
    setEditingTest(null)
    fetchTests()
  }

  useEffect(() => {
    setPage(1)
    setPerPage(viewMode === 'card' ? 9 : 20)
  }, [viewMode])

  const totalPages = getTotalPages(filteredTests.length, perPage)
  const paginatedTests = paginate(filteredTests, page, perPage)

  const formatDate = (isoDate) => new Date(isoDate).toLocaleDateString('tr-TR')

  const handleDuplicateTest = async (testId) => {
    try {
      // 1. Fetch the original test
      const { data: originalTest, error: testError } = await supabase
        .from('tests')
        .select('*')
        .eq('id', testId)
        .single()

      if (testError) throw testError

      // 2. Create a new test with duplicated properties
      const { data: newTest, error: insertError } = await supabase
        .from('tests')
        .insert({
          name: `${originalTest.name} (Kopya)`,
          description: originalTest.description,
          time_limit: originalTest.time_limit,
          categories: originalTest.categories,
          randomized: originalTest.randomized,
          type: originalTest.type,
          total_questions: originalTest.total_questions,
        })
        .select()
        .single()

      if (insertError) throw insertError

      if (newTest.type === 'manual' || newTest.type === 'hybrid') {
        // Fetch all questions from the original test for manual and hybrid tests
        const { data: originalQuestions, error: questionsError } = await supabase
          .from('test_questions')
          .select('*')
          .eq('test_id', testId)

        if (questionsError) throw questionsError

        // 4. Duplicate questions if any exist
        if (originalQuestions && originalQuestions.length > 0) {
          const duplicatedQuestions = originalQuestions.map((question) => ({
            test_id: newTest.id,
            question_id: question.question_id,
          }))

          const { error: questionsInsertError } = await supabase
            .from('test_questions')
            .insert(duplicatedQuestions)

          if (questionsInsertError) throw questionsInsertError
        }
      }

      toast.success('Sınav başarıyla kopyalandı')
      fetchTests() // Refresh the tests list
    } catch (error) {
      console.error('Error duplicating test:', error)
      toast.error('Sınav kopyalanırken hata oluştu')
    }
  }

  const handleTestDelete = async (test) => {
    try {
      if (test.type === 'manual' || test.type === 'hybrid') {
        const { error: tqError } = await supabase
          .from('test_questions')
          .delete()
          .eq('test_id', test.id)

        if (tqError) {
          console.error('Error deleting test_questions:', tqError)
          throw tqError
        }
      }

      const { error } = await supabase.from('tests').delete().eq('id', test.id)

      if (error) throw error

      toast.success('Sınav başarıyla silindi')
      fetchTests()
    } catch (error) {
      console.error('Error deleting test:', error)
      toast.error('Sınav silinirken hata oluştu')
    }
  }

  return (
    <TooltipProvider>
      <AdminLayout>
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <h1 className="text-2xl font-bold">Sınav Yönetimi</h1>
            <Button onClick={handleAddTest}>
              <Plus className="mr-2 h-4 w-4" />
              Yeni Sınav Oluştur
            </Button>
          </div>

          {isAddingTest ? (
            <TestForm onComplete={handleFormComplete} editingTest={editingTest} />
          ) : (
            <>
              <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-3">
                <div className="flex items-center gap-2 w-full md:w-auto">
                  <Input
                    placeholder="Sınavları ara..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    className="max-w-md"
                  />
                  <Button onClick={handleSearch} variant="outline" className="shrink-0">
                    <Search className="h-4 w-4 mr-1" />
                    Ara
                  </Button>
                </div>
                <div className="flex items-center gap-2 justify-end w-full md:w-auto mt-2 md:mt-0">
                  <Button
                    variant={viewMode === 'card' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setViewMode('card')}
                  >
                    <LayoutGrid className="h-4 w-4 mr-1" /> Kart
                  </Button>
                  <Button
                    variant={viewMode === 'list' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setViewMode('list')}
                  >
                    <ListIcon className="h-4 w-4 mr-1" /> Liste
                  </Button>
                  <div className="flex items-center gap-2 ml-4">
                    <span className="text-sm text-gray-500">Sayfa başı:</span>
                    <Select
                      value={perPage.toString()}
                      onValueChange={(v) => {
                        setPerPage(Number(v))
                        setPage(1)
                      }}
                    >
                      <SelectTrigger className="w-20">
                        <SelectValue placeholder="Sayfa başı" />
                      </SelectTrigger>
                      <SelectContent>
                        {(viewMode === 'card' ? [6, 9, 15] : [10, 20, 50, 100]).map((opt) => (
                          <SelectItem key={opt} value={opt.toString()}>
                            {opt}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="bg-blue-50 px-4 py-2 rounded-lg border border-blue-200 ml-4">
                    <p className="text-sm font-medium text-blue-800">
                      Toplam Atanmış Sınav: <span className="font-bold">{totalTestCount}</span>
                    </p>
                  </div>
                </div>
              </div>

              {/* İçerik */}
              {loading ? (
                <div className="text-center py-12">Yükleniyor...</div>
              ) : filteredTests.length > 0 ? (
                viewMode === 'card' ? (
                  // CARD VIEW
                  <div className="grid gap-4 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
                    {paginatedTests.map((test) => (
                      <Card key={test.id} className="overflow-hidden">
                        <CardContent className="p-0">
                          <div className="p-4 bg-gray-50 flex justify-between items-center">
                            <div className="flex items-center gap-3">
                              <div className="bg-gray-200 w-10 h-10 rounded-full flex items-center justify-center">
                                <BookOpen className="h-5 w-5 text-gray-500" />
                              </div>
                              <div>
                                <h3 className="font-medium">{test.name}</h3>
                              </div>
                            </div>
                            <div className="flex gap-1">
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleTestEdit(test)}
                                    disabled={assignmentCounts[test.id] > 0}
                                    className={
                                      assignmentCounts[test.id] > 0
                                        ? 'cursor-not-allowed opacity-50'
                                        : ''
                                    }
                                  >
                                    <Pencil className="h-4 w-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>
                                    {assignmentCounts[test.id] > 0
                                      ? 'Bu sınav atanmış olduğu için düzenlenemez'
                                      : 'Sınavı düzenle'}
                                  </p>
                                </TooltipContent>
                              </Tooltip>

                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDuplicateTest(test.id)}
                                  >
                                    <Copy className="h-4 w-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Sınavı kopyala</p>
                                </TooltipContent>
                              </Tooltip>

                              <AlertDialog>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <AlertDialogTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        disabled={assignmentCounts[test.id] > 0}
                                        className={
                                          assignmentCounts[test.id] > 0
                                            ? 'cursor-not-allowed opacity-50'
                                            : ''
                                        }
                                      >
                                        <Trash2 className="h-4 w-4 text-red-500" />
                                      </Button>
                                    </AlertDialogTrigger>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>
                                      {assignmentCounts[test.id] > 0
                                        ? 'Bu sınav atanmış olduğu için silinemez'
                                        : 'Sınavı sil'}
                                    </p>
                                  </TooltipContent>
                                </Tooltip>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Sınavı Sil</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      Bu sınavı silmek istediğinize emin misiniz? Bu işlem geri
                                      alınamaz.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>İptal</AlertDialogCancel>
                                    <AlertDialogAction onClick={() => handleTestDelete(test)}>
                                      Sil
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          </div>
                          <div className="p-4">
                            <p className="text-sm mb-4">{test.description}</p>
                            <div className="grid grid-cols-2 gap-4 mb-4">
                              <div className="bg-gray-50 p-2 rounded">
                                <div className="flex items-center gap-2">
                                  <Users className="h-4 w-4 text-gray-500" />
                                  <p className="text-sm">
                                    <span className="font-medium">
                                      {assignmentCounts[test.id] ?? 0}
                                    </span>{' '}
                                    Atama
                                  </p>
                                </div>
                              </div>
                              <div className="bg-gray-50 p-2 rounded">
                                <div className="flex items-center gap-2">
                                  <Users className="h-4 w-4 text-green-500" />
                                  <p className="text-sm">
                                    <span className="font-medium">
                                      {completedCounts[test.id] ?? 0}
                                    </span>{' '}
                                    Tamamlandı
                                  </p>
                                </div>
                              </div>
                            </div>
                            <div className="mt-4 pt-4 border-t">
                              <p className="text-sm text-gray-500">Oluşturulma Tarihi</p>
                              <p>{formatDate(test.created_at)}</p>
                            </div>
                            <div className="mt-4">
                              <Link to={`/admin/tests/${test.id}`}>
                                <Button variant="outline" className="w-full">
                                  Detayları Görüntüle
                                </Button>
                              </Link>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  // LIST VIEW
                  <div className="overflow-x-auto">
                    <div className="rounded-2xl shadow-lg border border-gray-200 bg-white overflow-hidden my-4">
                      <table className="min-w-full bg-white">
                        <thead>
                          <tr className="bg-gray-100">
                            <th className="p-4 text-left rounded-tl-2xl">Sınav Adı</th>
                            <th className="p-4 text-left">Açıklama</th>
                            <th className="p-4 text-left">Atama</th>
                            <th className="p-4 text-left">Tamamlandı</th>
                            <th className="p-4 text-left">Oluşturulma</th>
                            <th className="p-4 rounded-tr-2xl">İşlemler</th>
                          </tr>
                        </thead>
                        <tbody>
                          {paginatedTests.map((test, idx) => (
                            <tr key={test.id} className="hover:bg-gray-50 transition">
                              <td className="p-4 border-b border-gray-100">{test.name}</td>
                              <td className="p-4 border-b border-gray-100">
                                {test.description || '-'}
                              </td>
                              <td className="p-4 border-b border-gray-100">
                                {assignmentCounts[test.id] ?? 0}
                              </td>
                              <td className="p-4 border-b border-gray-100">
                                {completedCounts[test.id] ?? 0}
                              </td>
                              <td className="p-4 border-b border-gray-100">
                                {formatDate(test.created_at)}
                              </td>
                              <td className="p-4 border-b border-gray-100">
                                <div className="flex gap-2">
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => handleTestEdit(test)}
                                        disabled={assignmentCounts[test.id] > 0}
                                        className={
                                          assignmentCounts[test.id] > 0
                                            ? 'cursor-not-allowed opacity-50'
                                            : ''
                                        }
                                      >
                                        <Pencil className="h-4 w-4" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p>
                                        {assignmentCounts[test.id] > 0
                                          ? 'Bu sınav atanmış olduğu için düzenlenemez'
                                          : 'Sınavı düzenle'}
                                      </p>
                                    </TooltipContent>
                                  </Tooltip>

                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => handleDuplicateTest(test.id)}
                                      >
                                        <Copy className="h-4 w-4" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p>Sınavı kopyala</p>
                                    </TooltipContent>
                                  </Tooltip>

                                  <AlertDialog>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <AlertDialogTrigger asChild>
                                          <Button
                                            variant="outline"
                                            size="sm"
                                            disabled={assignmentCounts[test.id] > 0}
                                            className={
                                              assignmentCounts[test.id] > 0
                                                ? 'cursor-not-allowed opacity-50'
                                                : ''
                                            }
                                          >
                                            <Trash2 className="h-4 w-4 text-red-500" />
                                          </Button>
                                        </AlertDialogTrigger>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        <p>
                                          {assignmentCounts[test.id] > 0
                                            ? 'Bu sınav atanmış olduğu için silinemez'
                                            : 'Sınavı sil'}
                                        </p>
                                      </TooltipContent>
                                    </Tooltip>
                                    <AlertDialogContent>
                                      <AlertDialogHeader>
                                        <AlertDialogTitle>Sınavı Sil</AlertDialogTitle>
                                        <AlertDialogDescription>
                                          Bu sınavı silmek istediğinize emin misiniz? Bu işlem geri
                                          alınamaz.
                                        </AlertDialogDescription>
                                      </AlertDialogHeader>
                                      <AlertDialogFooter>
                                        <AlertDialogCancel>İptal</AlertDialogCancel>
                                        <AlertDialogAction onClick={() => handleTestDelete(test)}>
                                          Sil
                                        </AlertDialogAction>
                                      </AlertDialogFooter>
                                    </AlertDialogContent>
                                  </AlertDialog>

                                  <Link to={`/admin/tests/${test.id}`}>
                                    <Button variant="outline" size="sm">
                                      Detay
                                    </Button>
                                  </Link>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )
              ) : (
                <div className="text-center py-12">
                  <p className="text-gray-500 mb-4">
                    {searchQuery
                      ? 'Arama kriterine uygun sınav bulunamadı.'
                      : 'Henüz sınav oluşturulmamış.'}
                  </p>
                  {searchQuery && (
                    <Button variant="outline" onClick={() => setSearchQuery('')}>
                      Aramayı Temizle
                    </Button>
                  )}
                </div>
              )}

              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="flex justify-center items-center gap-2 mt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.max(p - 1, 1))}
                    disabled={page === 1}
                  >
                    Önceki
                  </Button>
                  {getPaginationPages(page, totalPages).map((pg, idx) =>
                    pg === '...' ? (
                      <span key={`ellipsis-${idx}`} className="px-2 text-gray-400">
                        ...
                      </span>
                    ) : (
                      <Button
                        key={pg + '-' + idx}
                        variant={page === pg ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setPage(Number(pg))}
                      >
                        {pg}
                      </Button>
                    )
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
                    disabled={page === totalPages}
                  >
                    Sonraki
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </AdminLayout>
    </TooltipProvider>
  )
}

export default AdminTests
